<template>
  <span
    class="villa-logo"
    :class="[`villa-logo--${variant}`, { 'villa-logo--animated': animated }]"
    :style="rootStyle"
    role="img"
    aria-label="VillaOS"
  >
    <!-- Tetto stilizzato (SVG, gradiente navy -> blu medio) -->
    <svg
      class="villa-logo__roof"
      viewBox="0 0 120 46"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          :id="gradId"
          x1="0"
          y1="0"
          x2="120"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stop-color="#082b57" />
          <stop offset="1" stop-color="#2f6fae" />
        </linearGradient>
      </defs>
      <polyline
        class="villa-logo__roof-line"
        points="8,41 60,7 112,41"
        :stroke="`url(#${gradId})`"
        stroke-width="11"
        stroke-linecap="square"
        stroke-linejoin="miter"
      />
    </svg>

    <!-- Wordmark testuale (font reale, non immagine) -->
    <span class="villa-logo__word" aria-hidden="true">
      <span class="villa-logo__villa">Villa</span
      ><span class="villa-logo__os">OS</span>
    </span>
  </span>
</template>

<script>
// Contatore a livello di modulo per generare id gradiente univoci
// (evita collisioni quando il logo è presente più volte nella pagina).
let _villaLogoUid = 0;

export default {
  name: "VillaLogo",
  props: {
    // "full"   -> tetto sopra, wordmark grande (login/splash/dashboard)
    // "compact"-> versione ridotta per header/sidebar
    variant: {
      type: String,
      default: "full",
      validator: (v) => ["full", "compact"].includes(v),
    },
    // Altezza del wordmark in px (il resto scala in proporzione)
    size: {
      type: Number,
      default: null,
    },
    // Abilita l'animazione del tetto via CSS (usata dallo splash)
    animated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      gradId: `villa-roof-grad-${++_villaLogoUid}`,
    };
  },
  computed: {
    rootStyle() {
      const fontSize = this.size ?? (this.variant === "compact" ? 24 : 64);
      return { "--villa-logo-size": `${fontSize}px` };
    },
  },
};
</script>

<style scoped>
.villa-logo {
  --villa-logo-size: 64px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
  user-select: none;
  gap: 0.12em;
  font-size: var(--villa-logo-size);
}

.villa-logo__roof {
  display: block;
  width: 2.1em;
  height: auto;
}

.villa-logo__roof-line {
  /* Per l'animazione "disegno da sinistra a destra" */
  stroke-dasharray: 200;
  stroke-dashoffset: 0;
}

.villa-logo__word {
  font-family: var(--villa-font-serif, "Playfair Display", Georgia, serif);
  font-weight: 700;
  font-size: 1em;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.villa-logo__villa {
  color: var(--villa-navy, #082b57);
}

.villa-logo__os {
  color: var(--villa-blue, #2f6fae);
}

/* ---- Variante compatta (header/sidebar): tetto e testo affiancati ---- */
.villa-logo--compact {
  flex-direction: row;
  gap: 0.4em;
}

.villa-logo--compact .villa-logo__roof {
  width: 1.35em;
}

/* ---- Animazione tetto (solo quando richiesta, es. splash) ---- */
.villa-logo--animated .villa-logo__roof-line {
  animation: villa-roof-draw 0.9s ease-out both;
}

@keyframes villa-roof-draw {
  from {
    stroke-dashoffset: 200;
  }
  to {
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .villa-logo--animated .villa-logo__roof-line {
    animation: none;
  }
}
</style>
